import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, Mail, ShoppingBag } from 'lucide-react';
import Profile from '../UserProfile/Profile';
import api from '../../../Api/Config'; // Import the API utility

function Header({ user, setUser, onLoginClick, onSignUpClick }) {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
  }, [setUser]);

  // Listen for logout events
  useEffect(() => {
    const handleLogoutEvent = () => {
      setUser(null);
      setToggleMenu(false);
      setDropdownOpen(false);
    };

    window.addEventListener('logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('logout', handleLogoutEvent);
    };
  }, [setUser]);

  // Validate token on component mount and periodically
  useEffect(() => {
    const validateUserToken = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Make a simple authenticated request to check token validity
          await api.get('/auth/me'); // You need to create this endpoint
        } catch (error) {
          // Token is invalid or backend is down, user will be logged out automatically by interceptor
          console.log('Token validation failed');
        }
      }
    };

    // Validate immediately
    validateUserToken();

    // Set up periodic token validation (every 5 minutes)
    const intervalId = setInterval(validateUserToken, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => setDropdownOpen(false), [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage and state
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setToggleMenu(false);
      setDropdownOpen(false);

      window.dispatchEvent(new Event('logout'));
    }
  };

  const handleLinkClick = () => setToggleMenu(false);

  // Rest of your component remains the same...
  return (
    <header className={`px-4 sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-xl py-4" : "shadow-lg py-6"}`}>
      {/* Your existing JSX remains unchanged */}
      <div className="container mx-auto max-w-6xl flex justify-between items-center">
        {/* Logo */}
        <div className="font-bold text-2xl font-mono text-redtxt hover:text-darktxt transition-colors">
          GrabGrub
        </div>

        {/* Desktop Links */}
        <ul className="hidden md:flex gap-8 items-center">
          <li>
            <Link
              to="/"
              className="text-darktxt font-semibold relative hover:text-redtxt hover:after:w-full after:content-[''] after:absolute after:bottom-[-5px] after:left-1/2 after:transform after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-redtxt after:transition-all after:duration-300"
            >
              Home
            </Link>
          </li>

          <li>
            <Link
              to="/restaurants"
              className="text-darktxt font-semibold relative hover:text-redtxt hover:after:w-full after:content-[''] after:absolute after:bottom-[-5px] after:left-1/2 after:transform after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-redtxt after:transition-all after:duration-300"
            >
              Restaurants
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              className="text-darktxt font-semibold relative hover:text-redtxt hover:after:w-full after:content-[''] after:absolute after:bottom-[-5px] after:left-1/2 after:transform after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-redtxt after:transition-all after:duration-300"
            >
              About
            </Link>
          </li>
          {user?.role === "admin" && (
            <li>
              <Link
                to="/admin"
                className="text-darktxt font-semibold relative hover:text-redtxt hover:after:w-full after:content-[''] after:absolute after:bottom-[-5px] after:left-1/2 after:transform after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-redtxt after:transition-all after:duration-300"
              >
                Admin
              </Link>
            </li>
          )}
          {user?.role === "restaurant" && (
            <li>
              <Link
                to="/restaurantmanage"
                className="text-darktxt font-semibold relative hover:text-redtxt hover:after:w-full after:content-[''] after:absolute after:bottom-[-5px] after:left-1/2 after:transform after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-redtxt after:transition-all after:duration-300"
              >
                Restaurant Manage
              </Link>
            </li>
          )}
        </ul>

        {/* Auth Buttons / Profile */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/cart">
            <ShoppingCart className="relative w-6 h-6 text-darktxt hover:text-redtxt transition-colors" />
          </Link>
          {!user ? (
            <>
              <button
                className="bg-white text-redtxt border border-white text-sm md:text-base font-semibold px-4 py-1.5 rounded-sm shadow-md hover:shadow-lg transition-all"
                onClick={onLoginClick}>
                Login
              </button>
              <button
                className="bg-redbg text-snowtxt border border-redtxt text-sm md:text-base font-semibold px-4 py-1.5 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600"
                onClick={onSignUpClick}>
                Sign Up
              </button>
            </>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <div className="flex items-center gap-3">
                <span className="text-darktxt font-semibold">{user.name}</span>
                <div
                  className="w-9 h-9 flex justify-center items-center text-snowtxt font-bold cursor-pointer bg-redbg rounded-full hover:bg-darkbg transition-colors"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                >
                  {user.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <Profile
                user={user}
                isOpen={dropdownOpen}
                onClose={() => setDropdownOpen(false)}
                onLogout={handleLogout}
              />
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Menu className="text-darktxt" onClick={() => setToggleMenu(true)} />
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {toggleMenu && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={() => setToggleMenu(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full bg-snowbg z-50 w-[70%] max-[425px]:w-full transform transition-transform duration-700 ease-in-out ${toggleMenu ? 'translate-x-0' : 'translate-x-full'
          }`}>
        <div className="p-6">
          <div className="flex justify-end mb-5">
            <X
              onClick={() => setToggleMenu(false)}
              className="text-darkbg w-6 h-6"
            />
          </div>
          <ul className="mb-5">
            <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
              <Link to="/" onClick={handleLinkClick}>
                Home
              </Link>
            </li>
            <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
              <Link to="/restaurants" onClick={handleLinkClick}>
                Restaurants
              </Link>
            </li>
            {user?.role === 'admin' && (
              <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
                <Link to="/admin" onClick={handleLinkClick}>
                  Admin
                </Link>
              </li>
            )}
            {user?.role === 'restaurant' && (
              <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
                <Link to="/restaurantmanage" onClick={handleLinkClick}>
                  Restaurants Manage
                </Link>
              </li>
            )}
            <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
              <Link to="/cart" onClick={handleLinkClick}>
                <ShoppingCart className="w-6 h-6 hover:text-redtxt" />
              </Link>
            </li>
            {user && (
              <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
                <Link to="/profile" onClick={handleLinkClick}>
                  Manage Profile
                </Link>
              </li>
            )}
            {user && (
              <li className="h-12 border-b border-gray-300 flex items-center text-lightGray">
                <Link to="/userorders" onClick={handleLinkClick}>
                  Order History
                </Link>
              </li>
            )}
          </ul>

          <div className="mb-5 border-b">
            <p className="text-xl pb-2 text-lightGray font-semibold">
              User Profile
            </p>
            <div>
              <div className="flex items-center gap-2 pb-2">
                <User className="w-5 h-5 text-lightGray" />
                <p className="text-lightGray">{user?.name || 'Guest'}</p>
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Mail className="w-5 h-5 text-lightGray" />
                <p className="text-lightGray">{user?.email || 'guest'}</p>
              </div>
            </div>
          </div>

          {!user ? (
            <div className="flex flex-col items-center justify-center gap-3 mb-5">
              <button
                type="button"
                className="w-full bg-white text-redtxt border border-white text-sm md:text-base font-semibold px-8 py-1.5 rounded-sm shadow-md hover:shadow-lg transition-all"
                onClick={() => {
                  onLoginClick();
                  setToggleMenu(false);
                }}>
                Login
              </button>
              <button
                type="button"
                className="w-full bg-redtxt text-white border border-redtxt text-sm md:text-base font-semibold px-8 py-1.5 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600"
                onClick={() => {
                  onSignUpClick();
                  setToggleMenu(false);
                }}>
                Sign Up
              </button>
            </div>
          ) : null}

          {user && (
            <div className="flex flex-col items-center justify-center gap-3 mb-5">
              <button
                type="button"
                className="w-full bg-redtxt text-white border border-redtxt text-sm md:text-base font-semibold px-8 py-1.5 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600"
                onClick={handleLogout}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;